import { showAlert } from "@/lib/alert";
import { MAX_UPLOAD_SIZE, type RNFile } from "@/lib/validation/file";
import * as DocumentPicker from "expo-document-picker";
import { File } from "expo-file-system";
import * as ImageManipulator from "expo-image-manipulator";
import * as ImagePicker from "expo-image-picker";
import { Platform } from "react-native";

const HEIC_MIME_TYPES = ["image/heic", "image/heif", "image/avif"];

const DEFAULT_IMAGE_OPTIONS: ImagePicker.ImagePickerOptions = {
  mediaTypes: ["images"],
  quality: 0.8,
};

export const imagePickerPresets = {
  document: { allowsEditing: false } satisfies ImagePicker.ImagePickerOptions,
  avatar: { allowsEditing: false, aspect: [1, 1] as [number, number] },
  vehicle: { allowsEditing: false, aspect: [4, 3] as [number, number] },
};

export type ImagePickerAsset = ImagePicker.ImagePickerAsset;

export async function requestMediaLibraryPermission(
  deniedMessage = "We need access to your photo library.",
): Promise<boolean> {
  const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (status !== "granted") {
    showAlert("Permission Required", deniedMessage);
    return false;
  }
  return true;
}

export async function requestCameraPermission(
  deniedMessage = "We need access to your camera.",
): Promise<boolean> {
  const { status } = await ImagePicker.requestCameraPermissionsAsync();
  if (status !== "granted") {
    showAlert("Permission Required", deniedMessage);
    return false;
  }
  return true;
}

export async function pickImageFromLibrary(
  options: ImagePicker.ImagePickerOptions = {},
  requestPermission = false,
  permissionMessage?: string,
): Promise<ImagePicker.ImagePickerAsset | null> {
  if (
    requestPermission &&
    !(await requestMediaLibraryPermission(permissionMessage))
  ) {
    return null;
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    ...DEFAULT_IMAGE_OPTIONS,
    ...options,
  });

  if (result.canceled || !result.assets[0]) {
    return null;
  }

  return result.assets[0];
}

export async function pickImageFromCamera(
  options: ImagePicker.ImagePickerOptions = {},
  requestPermission = false,
  permissionMessage?: string,
): Promise<ImagePicker.ImagePickerAsset | null> {
  if (
    requestPermission &&
    !(await requestCameraPermission(permissionMessage))
  ) {
    return null;
  }

  const result = await ImagePicker.launchCameraAsync({
    ...DEFAULT_IMAGE_OPTIONS,
    ...options,
  });

  if (result.canceled || !result.assets[0]) {
    return null;
  }

  return result.assets[0];
}

export async function pickDocumentFile(
  type: string[] = ["image/*", "application/pdf"],
): Promise<ImagePicker.ImagePickerAsset | null> {
  const result = await DocumentPicker.getDocumentAsync({
    type,
    copyToCacheDirectory: true,
  });

  if (result.canceled || !result.assets[0]) {
    return null;
  }

  const document = result.assets[0];

  return {
    uri: document.uri,
    width: 0,
    height: 0,
    fileName: document.name,
    fileSize: document.size,
    mimeType: document.mimeType,
  };
}

export function webFileToAsset(
  file: Blob & { name: string },
): ImagePicker.ImagePickerAsset {
  return {
    uri: URL.createObjectURL(file),
    width: 0,
    height: 0,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || undefined,
  };
}

export async function pickImagesFromLibrary(
  options: ImagePicker.ImagePickerOptions = {},
  requestPermission = false,
  permissionMessage?: string,
): Promise<ImagePicker.ImagePickerAsset[]> {
  if (
    requestPermission &&
    !(await requestMediaLibraryPermission(permissionMessage))
  ) {
    return [];
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    ...DEFAULT_IMAGE_OPTIONS,
    ...options,
  });

  if (result.canceled) {
    return [];
  }

  return result.assets;
}

export async function recordVideoFromCamera(
  options: ImagePicker.ImagePickerOptions = {},
  requestPermission = false,
  permissionMessage?: string,
): Promise<ImagePicker.ImagePickerAsset | null> {
  if (
    requestPermission &&
    !(await requestCameraPermission(permissionMessage))
  ) {
    return null;
  }

  const result = await ImagePicker.launchCameraAsync({
    mediaTypes: ["videos"],
    allowsEditing: false,
    videoMaxDuration: 60,
    videoQuality: ImagePicker.UIImagePickerControllerQualityType.Medium,
    ...options,
  });

  if (result.canceled || !result.assets[0]) {
    return null;
  }

  return result.assets[0];
}

function isHeicAsset(asset: ImagePicker.ImagePickerAsset): boolean {
  const mime = asset.mimeType?.toLowerCase() ?? "";
  const name = asset.fileName?.toLowerCase() ?? asset.uri.toLowerCase();

  return HEIC_MIME_TYPES.includes(mime) || /\.(heic|heif|avif)$/.test(name);
}

export async function normalizeImageAsset(
  asset: ImagePicker.ImagePickerAsset,
): Promise<ImagePicker.ImagePickerAsset> {
  const actions: ImageManipulator.Action[] = [];

  if (asset.width && asset.width > 1920) {
    actions.push({ resize: { width: 1920 } });
  }

  const shouldConvertHeic = isHeicAsset(asset);
  const shouldResize = actions.length > 0;

  if (!shouldConvertHeic && !shouldResize) {
    return asset;
  }

  const result = await ImageManipulator.manipulateAsync(
    asset.uri,
    actions,
    {
      format: ImageManipulator.SaveFormat.JPEG,
      compress: 0.7,
    },
  );

  const baseName = (asset.fileName ?? "image").replace(/\.(heic|heif|avif)$/i, "");

  return {
    ...asset,
    uri: result.uri,
    mimeType: "image/jpeg",
    fileName: `${baseName}.jpeg`,
    fileSize: await readConvertedFileSize(result.uri, asset.fileSize),
  };
}

async function readConvertedFileSize(
  uri: string,
  fallback: number | undefined,
): Promise<number | undefined> {
  try {
    return (await readLocalFileSize(uri)) ?? fallback;
  } catch (error) {
    console.error("Failed to read converted image size:", error);
    return fallback;
  }
}

export async function readLocalFileSize(uri: string): Promise<number | null> {
  if (Platform.OS === "web") {
    const response = await fetch(uri);
    const blob = await response.blob();

    return blob.size;
  }

  const file = new File(uri);

  return file.exists ? file.size : null;
}

export function assetToRNFile(
  asset: ImagePicker.ImagePickerAsset,
  fallbackName: string,
): RNFile {
  return {
    name: asset.fileName || `${fallbackName}_${Date.now()}`,
    size: asset.fileSize!,
    type: asset.mimeType!,
    uri: asset.uri,
  };
}

export function validateFileSize(asset: ImagePicker.ImagePickerAsset): {
  isValid: boolean;
  message: string | null;
} {
  if (asset.fileSize! > MAX_UPLOAD_SIZE) {
    return {
      isValid: false,
      message: `File size ${formatBytesToMB(asset.fileSize!)} is greater than ${formatBytesToMB(MAX_UPLOAD_SIZE)}`,
    };
  } else {
    return {
      isValid: true,
      message: null,
    };
  }
}

export function formatBytesToMB(bytes: number, decimals = 2): string {
  if (!bytes || bytes < 0) return "0 MB";

  return `${(bytes / (1024 * 1024)).toFixed(decimals)} MB`;
}
